import { dayMonthLabel } from "./calendar";
import type { WorkoutPreview } from "./workoutPreview";

/* THE CLIENT WEEK'S DAY CELLS — W72 step 2 (Proposal 2 with his three changes, 2026-09-25).
 *
 * His words: *"make the containers all square … when square they will hide what exercises they contain,
 * but when expanded manually each one, or even with a global expand button they will show. if all
 * expanded, the button will turn as opposite function and vice versa."* 🔴 SUPERSEDED BY W107 (William, his coach,
 * 2026-09-30: *"when im designing a program, i need to see all the workouts in the week, so i can clearly see
 * everything"*): the cells no longer open and close — every card draws its whole list, always (`dayExerciseLines`).
 *
 * ⚠️ PURE, and the component asks it for everything: which state a cell is in, what its head says and what its list
 * holds. A rule in the component is a rule no test can reach.
 */

/** The cell's five states. `today` is the day itself; done / missed / assigned / rest are what it holds. */
export type CellState = "rest" | "assigned" | "today" | "done" | "missed";

/**
 * Which state a day is in. Only TRAINING counts — a task-only day is `rest` (a task is reported, never
 * scored: the W71 fold says it on the cell). A trained day is `done` whether or not it was assigned; a
 * past assigned day with nothing logged is `missed`; today's untrained work is `today`; the future is
 * `assigned`.
 */
export function cellState(d: {
  isToday: boolean; isPast: boolean; trained: boolean; hasTraining: boolean;
}): CellState {
  if (d.trained) return "done";
  if (!d.hasTraining) return d.isToday ? "today" : "rest";
  if (d.isToday) return "today";
  return d.isPast ? "missed" : "assigned";
}

/* W107 (William, 2026-09-30: "when im designing a program, i need to see all the workouts in the week") — THE OPEN /
   CLOSED STATE IS RETIRED. His 2026-09-25 square cells that opened one by one (and `Open all`, `?open=all`) hid exactly
   what he asked to see; every day card now draws its whole list, always, so the state has nothing to switch. */

const FULL_WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const SHORT_WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** The cell's head: `Monday 21/9` at ONE week, `Wed 23/9` at two and four (Fable, step 2 FIX 4: the full
 *  word wrapped in a 2-week cell). ⚠️ The `21/9` half IS `calendar.dayMonthLabel` (W71's one formatter,
 *  shared with the Tasks calendar), never a second spelling. */
export function cellHead(dayKey: string, span: 1 | 2 | 4 = 1): string {
  const d = new Date(`${dayKey}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  const word = span === 1 ? FULL_WEEKDAY[d.getUTCDay()] : SHORT_WEEKDAY[d.getUTCDay()];
  return `${word} ${dayMonthLabel(dayKey)}`;
}

/** The head's WORD alone (`Monday` / `Wed`), so the phone — whose row gutter already says `MON` — can drop it
 *  without a second spelling of the date. `cellHead` is this word + `dayMonthLabel`. */
export function cellHeadWord(dayKey: string, span: 1 | 2 | 4 = 1): string {
  const d = new Date(`${dayKey}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return (span === 1 ? FULL_WEEKDAY[d.getUTCDay()] : SHORT_WEEKDAY[d.getUTCDay()]) ?? "";
}

/** One exercise's line on the day card: its name and its SHORT dose (Fable fix (a): `2 × 15 @ 12.5 kg`), in the parts
 *  `collapsedLabelParts(sets, "short")` returns — so a narrow card may break BETWEEN the count and the load and never
 *  inside either, and nothing is cut. */
export type DayExerciseLine = { readonly id: string; readonly name: string; readonly doseParts: readonly string[] };

/**
 * 🔴 W107 — THE DAY CARD'S LIST: EVERY exercise, in the workout's order, `name` + dose. The dose is the preview's `dose`,
 * which IS `collapsedLabel` (`workoutPreview.previewRow`) — the ONE one-line prescription, never a second spelling (W72
 * step 2's `exerciseLine` said only `· N sets`, which told a coach designing a program nothing about the reps or the load).
 * Read off an UNCAPPED preview (`workoutOverview`), so a long day is never cut to the glance's cap.
 */
export function dayExerciseLines(preview: Pick<WorkoutPreview, "sections">): DayExerciseLine[] {
  return preview.sections.flatMap((s) => s.rows.map((r) => ({
    id: r.exerciseRowId, name: r.name, doseParts: r.shortDoseParts,
  })));
}
